import { Component, EventEmitter, Input, OnChanges, OnInit, Output, Sanitizer, SimpleChanges } from "@angular/core";
import { TranslationService } from "@app/services/translation.service";
import { DateService, DateUnit } from "@services/date.service";
import { CalendarService } from "@app/services/calendar.service";
import { CalendarDirection } from "@app/enum/CalendarDirection.enum";
import "./v-calendar.component.scss";

export interface IVCalendarMonth {
    date: Date;

    name: string;
    year: number;

    startingDay: number;
    daysInMonth: number;

    dayHeaders: string[],
    days: IVCalenderDay[];
}

export interface IVCalenderDay {
    day: number;
    date: Date;

    selected?: boolean;
    disabled?: boolean;
    today?: boolean;
    loading?: boolean;

    startOfRange?: boolean;
    endOfRange?: boolean;
}

@Component({
    selector: "v-calendar",
    template: require("./v-calendar.component.html")
})
export class VCalendarComponent implements OnInit, OnChanges {
    @Input() monthsToShow = 1;
    @Input() maxDays = 31; // Max selectable days
    @Input() direction: CalendarDirection = CalendarDirection.TodayPast;

    @Input() minDate: Date = new Date();
    @Input() date: Date;
    @Input() endDate: Date;
    @Input() allowedDates: Date[];

    @Input() loading: boolean;
    @Input() dayLoading: boolean;

    @Output() dateChanged: EventEmitter<Date> = new EventEmitter();
    @Output() dateRangeChanged: EventEmitter<Date[]> = new EventEmitter();

    // ---

    // new
    @Input() locale: string;

    months: IVCalendarMonth[];

    _startDate: Date;
    _endDate: Date;

    canNavigateForwards: boolean;
    canNavigateBackwards: boolean;

    allowRangeUpdateOnHover = false;

    invalidSelection: boolean;
    error: string = '';

    constructor(
        private readonly sanitizer: Sanitizer,
        protected readonly dateService: DateService,
        protected readonly translationService: TranslationService,
        private readonly calendarService: CalendarService) {
    }

    ngOnInit(): void {
        this.direction ??= CalendarDirection.TodayPast;

        if (!this.months) {
            this.initVCalendar();

            // Default to today
            this.setDates(new Date);
        }

        if (this.direction == CalendarDirection.AfterMinDate && this.minDate == null) {
            this.direction = CalendarDirection.TodayFuture;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["direction"] && changes["direction"].currentValue === undefined) {
            this.direction = CalendarDirection.TodayPast;
        }

        if (changes["date"] && changes["date"].isFirstChange) {
            this.initVCalendar();
        }

        let updateDayStates = false;

        if (changes["date"] || changes["endDate"]) {
            this.setDates(this.date, this.endDate);
            updateDayStates = true;
        }

        if (changes["dayLoading"]) {
            updateDayStates = true;
        }

        if (changes["direction"]) {
            this.updateDirectionStates();
        }

        if (updateDayStates) {
            this.updateDayStateForAllMonths();
        }
    }

    navigate(direction: number): void {
        var date = this.getNextMonth(this.months[0].date, direction);

        this.createVCalendar(date);
        this.updateDirectionStates();
    }

    hoverDay(day: IVCalenderDay): void {

        if (!this._startDate || !this.allowRangeUpdateOnHover) {
            return;
        }

        this._endDate = day.date;

        // If our date range is too long, then adjust to our maxDays
        while (Math.floor(this.dateService.getDiff(this._startDate, this._endDate, DateUnit.Day)) > this.maxDays) {
            this._endDate = this.dateService.addOrSubtractTime(this._startDate, 1, DateUnit.Day);
        }

        while (this.dateService.getDiff(this._startDate, this._endDate, DateUnit.Day) < -this.maxDays) {
            this._endDate = this.dateService.addOrSubtractTime(this._startDate, -1, DateUnit.Day);
        }

        this.updateDayStateForAllMonths();
    }

    dayClicked(day: IVCalenderDay): void {

        this.dateChanged.emit(day.date);

        if (this.maxDays === 1 && this._startDate) {
            this._startDate = this.dateService.getStartOfDay(day.date);
            this._endDate = this.dateService.getEndOfDay(day.date);

            this.allowRangeUpdateOnHover = false;
            this.updateDayStateForAllMonths();
            return;
        }

        if (!this.allowRangeUpdateOnHover && (this._endDate || !this._startDate)) {
            this._startDate = day.date;
            this._endDate = null;

            this.allowRangeUpdateOnHover = true;
        }
        else {
            // Set end date to now if selected day today and should not be able to select future dates, else set end date to end of selected day
            if (this.dateService.isSameDate(day.date, new Date()) && !this.canNavigateForwards) {
                this._endDate = new Date();
            }
            else {
                this._endDate = this.dateService.getEndOfDay(day.date);
            }

            this.checkDateRangeIsValid();

            this.allowRangeUpdateOnHover = false;
        }

        this.checkIfSelectionIsInvalid();
        this.dateRangeChanged.emit([this._startDate, this._endDate]);
        this.updateDayStateForAllMonths();
    }

    private checkIfSelectionIsInvalid(date?: Date): void {
        this.invalidSelection = false;
        this.error = undefined;

        if (this._startDate && !this._endDate) {
            this.invalidSelection = true;
            this.error = this.translationService.t("DateButtonFilter.PleaseSelectEnd");
        }

        // Note the floor is here to account for months with an odd number of hours, e.g. when clocks go back in October
        if (Math.floor(this.dateService.getDiff(this._startDate, this._endDate, DateUnit.Day)) > this.maxDays) {
            this.invalidSelection = true;
            this.error = this.translationService.t("DateButtonFilter.RangeGtThanAllowed", this.maxDays);
        }
    }

    private setDates(startDate: Date, endDate?: Date): void {
        if (!endDate) {
            endDate = startDate;
        }

        this._startDate = new Date(startDate);
        this._endDate = new Date(endDate);

        this._datesChanged();
    }

    private initVCalendar(): void {
        this.createVCalendar(new Date());
    }

    private createVCalendar(date: Date): void {
        var thisMonth = this.createCalendarForMonth(date);

        this.months = [thisMonth];

        var weekAgo = new Date();
        weekAgo.setDate(date.getDate() - 6);
    }

    private getNextMonth(date: Date, direction: number): Date {
        var adjustedDate = new Date(date);
        adjustedDate.setMonth(date.getMonth() + direction);

        return adjustedDate;
    }

    private createOrderedTimeRange(from: Date, to: Date): { from: Date, to: Date } {
        if (this.dateService.date1IsAfterDate2(from, to)) {
            return { from: to, to: from }
        }

        return { from: from, to: to }
    }

    private createCalendarForMonth(date: Date): IVCalendarMonth {
        // sanitize date to first day of month
        date.setDate(1);

        var daysInMonth = this.getDaysInMonth(date);
        var monthStartingDay = this.getStartingDayOfMonth(date);

        var calendarMonth: IVCalendarMonth = {
            date: date,
            name: date.toLocaleDateString(this.locale, { month: "long" }),
            year: date.getFullYear(),

            daysInMonth: daysInMonth,
            startingDay: monthStartingDay,

            dayHeaders: [],
            days: []
        };

        this.createDayHeaders(calendarMonth);
        this.createDays(calendarMonth);

        return calendarMonth;
    }

    private getDaysInMonth(date: Date): number {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    }

    private getStartingDayOfMonth(date: Date): number {
        const startingDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
        if (startingDayOfMonth === 0) {
            // Calendar week starts on a Monday so we want to put Sunday at the end
            return 7;
        }
        return startingDayOfMonth;
    }

    private createDayHeaders(calendarMonth: IVCalendarMonth): void {

        var startingDayOfWeek = 1;
        var dow = startingDayOfWeek;
        while (calendarMonth.dayHeaders.length < 7) {

            calendarMonth.dayHeaders.push(this.getDayOfWeekString(dow));

            if (++dow >= 7) {
                dow = 0;
            }
        }
    }

    private getDayOfWeekString(dayOfWeek: number): string {
        var date = new Date();

        while (date.getDay() != dayOfWeek) {
            date.setDate(date.getDate() + 1);
        }

        return date.toLocaleDateString(this.locale, { weekday: "short" });
    }

    private createDays(calendarMonth: IVCalendarMonth): void {

        for (var d = 1; d <= calendarMonth.daysInMonth; d++) {
            var date = this.dateService.getStartOfDay(calendarMonth.date);
            date.setDate(d);

            var day = { day: d, date: date };

            this.updateDayState(day);
            calendarMonth.days.push(day);
        }
    }

    private _datesChanged(): void {

        this.updateDayStateForAllMonths();

        const displayedDate = (this.dateService.allDatesAreValid([this._endDate])) ? this._endDate : this._startDate;

        // Ensure start of first month of selected days is visible
        while (this.months[0].date.getMonth() < displayedDate.getMonth()) {
            this.navigate(1);
        }

        while (this.months[0].date.getMonth() > displayedDate.getMonth() || this.months[0].date.getFullYear() > displayedDate.getFullYear()) {
            this.navigate(-1);
        }
    }

    private updateDayStateForAllMonths(): void {
        this.months.forEach(month => {
            month.days.forEach(day => {
                this.updateDayState(day);
            });
        });
    }

    private updateDayState(day: IVCalenderDay): void {
        day.today = this.dateService.isSameDate(day.date, new Date());
        day.loading = day.selected && this.dayLoading;
        day.disabled = this.dayLoading || !this.calendarService.isDayEnabled(day.date, this.direction, this.allowedDates, this.minDate);

        this.updateDaySelection(day);
    }

    private updateDaySelection(day: IVCalenderDay): void {

        if (!this._startDate || !this._endDate) {
            return;
        }

        var { from, to } = this.createOrderedTimeRange(this._startDate, this._endDate);

        day.startOfRange = this.dateService.isSameDate(from, day.date);
        day.endOfRange = this.dateService.isSameDate(to, day.date);
        day.selected = day.startOfRange
            || day.endOfRange
            || this.dateService.dateIsBetweenDates(day.date, from, to);
    }

    private updateDirectionStates(): void {

        this.canNavigateForwards = false;
        this.canNavigateBackwards = false;

        if (this.direction == CalendarDirection.Any) {
            this.canNavigateForwards = true;
            this.canNavigateBackwards = true;
        }
        else if (this.direction == CalendarDirection.AfterMinDate) {
            const endTotalMonths = this.months[0].date.getFullYear() * 12 + this.months[0].date.getMonth();
            const minTotalMonths = new Date(this.minDate).getFullYear() * 12 + new Date(this.minDate).getMonth();
            
            this.canNavigateForwards = true;
            this.canNavigateBackwards = endTotalMonths > minTotalMonths;
        }
        else if (this.direction == CalendarDirection.Future || this.direction == CalendarDirection.TodayFuture) {
            this.canNavigateForwards = true;
            const nextMonth = this.getNextMonth(this.months[0].date, -1);
            this.canNavigateBackwards = nextMonth > new Date() || this.dateService.isSameMonth(nextMonth, new Date());
        }
        else if (this.direction == CalendarDirection.Past || this.direction == CalendarDirection.TodayPast) {
            this.canNavigateForwards = this.getNextMonth(this.months[0].date, 1) < new Date();
            this.canNavigateBackwards = true;
        }

        this.updateDayStateForAllMonths();
    }

    private checkDateRangeIsValid(): void {

        if (this.dateService.date1IsAfterDate2(this._startDate, this._endDate)) {
            const tempStartDate = this._startDate;

            this._startDate = this.dateService.getStartOfDay(this._endDate);
            this._endDate = this.dateService.getEndOfDay(tempStartDate);
        }
    }
}
